import { Box, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Accordion from '@/components/Accordion'
import Button from '@/components/Button'
import DraggableContainer from '@/components/DraggableContainer'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import LoadingSpinner from '@/components/LoadingSpinner'
import Select from '@/components/Select'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  removeWashBakeTarget,
  setAvailableSpace,
  setSelectedWashbakeFrameworkKey,
  setWashBakeStainingBrand,
} from '@/store/stainingStudio'
import { REGION_WITH_COLOR, SLIDERRANGE } from '@/utils/constants'
import { getOptions } from '@/utils/helper'

interface StaingingStudioProps {
  dimensions: { width: number; height: number }
  setTooltipOpen: (tooltipOpen: boolean) => void
  setTooltipMessage: (tooltipMessage: string) => void
}

export interface Metadata {
  [key: string]: any
}

const NewWashBakePanel: React.FC<StaingingStudioProps> = ({
  dimensions,
  setTooltipOpen,
  setTooltipMessage,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()

  const PANEL_MIN_WIDTH = 778

  const washBake = useAppSelector((state) => state.stainingStudio.washBake)

  const selectedWashbakeFrameworkKey = useAppSelector(
    (state) => state.stainingStudio.selectedWashbakeFrameworkKey,
  )

  const selectedValues = useMemo(
    () =>
      [
        Object.keys(washBake?.[PLACEMENT.LEFT] || {}),
        Object.keys(washBake?.[PLACEMENT.RIGHT] || {}),
      ].flat(),
    [washBake],
  )

  const {
    data: metadata,
    error,
    isLoading,
  } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }

  const stainingBrands = useMemo(
    () => (metadata ? getOptions(metadata, 'available_stainingbrands') : []),
    [metadata],
  )

  const washBakeStainingBrand = useAppSelector(
    (state) => state.stainingStudio.washbakeStainingBrand,
  )

  const availableSpace = useAppSelector(
    (state) => state.stainingStudio.availableSpace,
  )

  if (error) {
    throw error
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <DraggableContainer
      axis="x"
      bounds="parent"
      defaultPosition={{
        x: dimensions?.width ? dimensions?.width - PANEL_MIN_WIDTH / 2 : 0,
        y: dimensions?.height,
      }}
    >
      <Accordion
        title={
          <LabelWithTooltip
            titleSx={{ fontSize: '18px' }}
            label={t('stainingCorrection.panel.generateRecipe')}
            tooltipText={t('stainingCorrection.panel.recipeTooltip')}
          />
        }
        sx={{
          background: theme.palette.common.black,
          minWidth: `${PANEL_MIN_WIDTH}px`,
          transform: 'translate(0%, -100%)',
          '&.MuiPaper-root': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
        accordionSummarySx={{
          padding: '8px 16px',
        }}
        titleSx={{
          fontSize: '20px',
        }}
        accordionDetailsSx={{
          padding: '16px 32px 24px 32px',
          maxHeight: '190px',
        }}
      >
        <Box display="flex" flexGrow={1} gap={4}>
          <Box display="flex">
            <Select
              options={stainingBrands}
              title={t('stainingCorrection.panel.stainingBrand')}
              tooltipText={t('stainingCorrection.panel.stainingBrandTooltip')}
              name="washBakeStainingBrand"
              onFormValueChange={(name, value) => {
                dispatch(setWashBakeStainingBrand(value))
              }}
              defaultValue={washBakeStainingBrand}
              sx={{
                width: '250px',
              }}
              titleSx={{ fontSize: '20px' }}
              titleSize="small"
            />
          </Box>
          <Box
            sx={{
              borderRight: '1px solid var(--color-border-table)',
              height: 'auto',
            }}
            height="auto"
            display="flex"
          ></Box>

          <Box display="flex" flexGrow={1} gap={2} flexDirection={'column'}>
            <Box display="flex" flexDirection="column">
              <LabelWithTooltip
                label="Available Space for Ceramic Layering"
                tooltipText={'Available Space for Ceramic Layering'}
                titleSx={{ fontSize: '16px' }}
                tooltipSx={{ height: '20px', width: '20px' }}
                fontSize="16px"
              />
              <Select
                options={SLIDERRANGE}
                name="category-select"
                onFormValueChange={(name, value) => {
                  dispatch(setAvailableSpace(value))
                }}
                defaultValue={availableSpace}
                sx={{ width: `250px` }}
                titleSize="small"
              />
            </Box>

            <Box display="flex" gap={1}>
              {Object.keys(REGION_WITH_COLOR).map((name) => (
                <Button
                  disableRipple
                  variant="contained"
                  key={name}
                  sx={{
                    maxWidth: `128px`,
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    color:
                      selectedWashbakeFrameworkKey === name ||
                      selectedValues?.includes(name)
                        ? theme.palette.common.black
                        : theme.palette.common.white,
                    fontWeight: 400,
                    background:
                      selectedWashbakeFrameworkKey === name ||
                      selectedValues?.includes(name)
                        ? REGION_WITH_COLOR[name]
                        : '#292929',
                    '&:hover': {
                      background: REGION_WITH_COLOR[name],
                      color: theme.palette.common.black,
                    },
                    fontSize: '14px',
                    maxHeight: '32px',
                  }}
                  label={name}
                  onClick={() => {
                    if (selectedValues?.includes(name)) {
                      dispatch(setSelectedWashbakeFrameworkKey(''))
                      dispatch(
                        removeWashBakeTarget({
                          key: name,
                        }),
                      )
                    } else {
                      dispatch(setSelectedWashbakeFrameworkKey(name))
                      setTooltipOpen(true)
                      setTooltipMessage(t('stainingCorrection.panel.firstStep'))
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Accordion>
    </DraggableContainer>
  )
}

export default NewWashBakePanel
